/**
 * Bearer Lifecycle Service
 *
 * The one owner of a bearer's lifetime under token auth: whether it is due for
 * a refresh, the refresh itself, and what a failed refresh means for the
 * session. `TokenRefreshService` speaks the protocol; this decides when to use
 * it and what follows.
 *
 * Nothing here is scheduled. The API refreshes a bearer that is STILL VALID, so
 * a refresh is something to do ahead of expiry — and the app cannot count on
 * being alive at a chosen moment thirty days out. Instead the platform asks
 * `evaluate()` at the moments it IS alive: when it starts, and when it returns
 * to the foreground.
 *
 * See docs/roadmap/token-refresh-window-and-scheduling.md.
 */

import { getTokenRefreshService } from './token.refresh.service';
import { getAuthConfig, getTokenStorage } from '../../di/container';
import { getSessionEpoch } from '../../store/session-scope';
import {
  syncApplyRefreshedToken,
  syncEndAuthSession,
} from '../../store/store-sync';
import {
  BEARER_REFRESH_WINDOW_MS,
  TokenRefreshError,
} from '../../types/auth/token.types';
import { dayjs } from '../../utils/date';

import type { TokenRefreshService } from './token.refresh.service';
import type { AuthToken } from '../../types/auth';
import type { ITokenStorageService } from '../../types/auth/storage.types';
import type { BearerVerdict } from '../../types/auth/token.types';

export class BearerLifecycleService {
  /**
   * The refresh in flight, if any. A refresh rotates the bearer — the API
   * deletes the old row — so two that overlap cannot both succeed: the second
   * would sign with a bearer the first has just killed. Every caller shares
   * this one.
   */
  private inFlight: Promise<AuthToken> | null = null;

  constructor(private customTokenRefreshService?: TokenRefreshService) {}

  /**
   * Look at the stored bearer and do what it calls for.
   *
   * Never throws: it runs unawaited, in the background of a start-up or a
   * return to the foreground, and the verdict is the whole answer.
   */
  public async evaluate(): Promise<BearerVerdict> {
    try {
      if (getAuthConfig().mode !== 'token') return 'inert';

      const tokenData = await this.tokenStorage().getTokenData();
      if (!tokenData) return 'inert';

      const now = dayjs().valueOf();

      // Past its expiry a bearer cannot sign its own refresh. There is nothing
      // to attempt; discarding the session is the caller's call.
      if (now > tokenData.expiresAt) return 'spent';

      if (now < tokenData.expiresAt - BEARER_REFRESH_WINDOW_MS) return 'fresh';

      await this.refresh();
      return 'refreshed';
    } catch (error) {
      if (error instanceof TokenRefreshError && error.reason === 'rejected') {
        return 'ended';
      }

      console.warn('[auth] Bearer refresh deferred:', error);
      return 'deferred';
    }
  }

  /**
   * Swap the stored bearer for a fresh one, whatever its age.
   *
   * Concurrent callers share one request. On success the new bearer reaches
   * token storage AND the auth store, so the expiry the store judges a session
   * by is never the replaced bearer's.
   *
   * A failure is a `TokenRefreshError`. Only `rejected` — the API refused the
   * bearer — ends the session; a dropped connection, a timeout or a 5xx leaves
   * it alone, because the bearer that failed to refresh is still good.
   */
  public refresh(): Promise<AuthToken> {
    if (!this.inFlight) {
      this.inFlight = this.performRefresh().finally(() => {
        this.inFlight = null;
      });
    }

    return this.inFlight;
  }

  /**
   * Resolves once no refresh is in flight — at once if none is. Never rejects:
   * it is for a caller that needs to know the bearer has stopped moving, not
   * how the refresh went.
   */
  public settled(): Promise<void> {
    if (!this.inFlight) return Promise.resolve();

    return this.inFlight.then(
      () => undefined,
      () => undefined
    );
  }

  /**
   * Forget a session the API has refused — store first, since that is what the
   * route guards read, then the stored tokens. Never throws.
   *
   * Public for the one other place that learns a bearer is finished: a request
   * refused with a 401 while the bearer it was signed with is still the one in
   * storage.
   */
  public async endSession(): Promise<void> {
    syncEndAuthSession();

    try {
      await this.tokenStorage().clearTokens();
    } catch (error) {
      console.warn('[auth] Could not clear the stored token:', error);
    }
  }

  private async performRefresh(): Promise<AuthToken> {
    // Whose refresh this is. A rejection that lands after the session that
    // asked has ended must not end the one that has since begun.
    const epoch = getSessionEpoch();

    try {
      const token = await this.tokenRefreshService().refreshToken();
      syncApplyRefreshedToken(token);
      return token;
    } catch (error) {
      if (
        error instanceof TokenRefreshError &&
        error.reason === 'rejected' &&
        getSessionEpoch() === epoch
      ) {
        await this.endSession();
      }
      throw error;
    }
  }

  private tokenRefreshService(): TokenRefreshService {
    return this.customTokenRefreshService || getTokenRefreshService();
  }

  private tokenStorage(): ITokenStorageService {
    return getTokenStorage();
  }
}

let instance: BearerLifecycleService | null = null;

/**
 * The shared lifecycle. One instance, because the in-flight guard only means
 * something if every caller goes through the same one.
 */
export const getBearerLifecycle = (): BearerLifecycleService => {
  if (!instance) {
    instance = new BearerLifecycleService();
  }
  return instance;
};
